import React, { useEffect } from 'react';
import { IconName, Icon } from 'app/common/components/controls';
import { Tooltip, TooltipPrimitiveLight } from '@trello/nachos/tooltip';
import classNames from 'classnames';
import styles from './FreeTrialPlanSelection.less';
import { forNamespace, asMoney } from 'app/common/lib/util/i18n';
import { OrganizationFeatures } from '@trello/product-features';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';
import { Analytics } from '@trello/atlassian-analytics';
import { FreeTrialTestIds } from '@trello/test-ids';

type PlanSelectionType = 'try-business-class' | 'have-business-class';
type TrialLength = 14 | 30;

const format = forNamespace('upgrade-prompt-plan-selection-consolidated');

const getPlanFeatureStrings = (plan: string, feature: string) => {
  const namespace = [plan, 'features', feature];

  return {
    title: plan === 'free-team' ? null : format([...namespace, 'title']),
    content: format([...namespace, 'content']),
    tooltip:
      plan === 'free-team' || feature === 'boards'
        ? null
        : format([...namespace, 'tooltip']),
  };
};

// exported for testing
export const bcPlanSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('bc-team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('bc-team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('bc-team', 'butler'),
  },
  {
    icon: IconName.Subscribe,
    ...getPlanFeatureStrings('bc-team', 'admin'),
  },
  {
    icon: IconName.Heart,
    ...getPlanFeatureStrings('bc-team', 'support'),
  },
  {
    icon: IconName.Star,
    ...getPlanFeatureStrings('bc-team', 'more'),
  },
];

const freeTeamSelectionList = [
  {
    icon: IconName.Board,
    ...getPlanFeatureStrings('free-team', 'boards'),
  },
  {
    icon: IconName.PowerUp,
    ...getPlanFeatureStrings('free-team', 'pups'),
  },
  {
    icon: IconName.ButlerBot,
    ...getPlanFeatureStrings('free-team', 'butler'),
  },
];

const renderTooltip = (tooltip: string | null, target: React.ReactNode) =>
  tooltip ? (
    <Tooltip content={tooltip} component={TooltipPrimitiveLight}>
      {target}
    </Tooltip>
  ) : (
    target
  );

interface PlanSelectionProps {
  orgId: string;
  type: PlanSelectionType;
  trialLength: TrialLength;
  cta: React.ReactNode;
}

const afterTrial = format('after-trial', {
  price: `$${asMoney(
    OrganizationFeatures.getPrice(
      OrganizationFeatures.currentProducts.yearly,
    )! / 12,
  )}`,
  downgradeOption: (
    <a
      key="after-trial-downgrade"
      target="_blank"
      href="https://help.trello.com/article/836-cancel-trello-business-class"
    >
      {format('after-trial-downgrade')}
    </a>
  ),
});

export const FreeTrialPlanSelection: React.FunctionComponent<PlanSelectionProps> = ({
  type,
  trialLength,
  cta,
  orgId,
}) => {
  const learnMoreBCButtonWithTracking = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'learnMoreAboutBCButton',
      source: 'planSelectionModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
  };

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'planSelectionModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
  }, [orgId]);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.FreeTrialExistingTeam,
      }}
    >
      <div
        data-test-id={
          type === 'have-business-class'
            ? FreeTrialTestIds.HaveBCPlanSelection
            : FreeTrialTestIds.TryBCPlanSelection
        }
        className={styles.heading}
      >
        <h1>
          <img
            src={require('resources/images/moonshot/bc-sparkle.svg')}
            alt=""
            aria-hidden="true"
          />
          {format([type, 'headline'], { dayCount: trialLength })}
        </h1>
        <h2>{format([type, 'description'], { dayCount: trialLength })}</h2>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.freePlan}>
          <h2>{format(['free-team', 'headline'])}</h2>
          <ul>
            {freeTeamSelectionList.map(({ icon, content }, index) => (
              <li key={index} className={styles.featureItem}>
                <Icon name={icon} medium className={styles.featureIcon} />
                <span className={styles.featureContent}>{content}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.bcPlan}>
          <div className={styles.recommendedLabel}>
            {format([type, 'tag'], { dayCount: trialLength })}
          </div>
          <h2>{format(['bc-team', 'headline'])}</h2>
          <ul>
            {bcPlanSelectionList.map(
              ({ icon, title, content, tooltip }, index) => (
                <li
                  key={index}
                  className={classNames(
                    styles.featureItem,
                    !!tooltip && styles.tooltipTarget,
                  )}
                >
                  {renderTooltip(
                    tooltip,
                    <div className={styles.featureItemWrapper}>
                      <Icon name={icon} medium className={styles.featureIcon} />
                      <div>
                        <span className={styles.featureTitle}>
                          {title} {''}
                          <span className={styles.featureContent}>
                            {content}
                          </span>
                        </span>
                      </div>
                    </div>,
                  )}
                </li>
              ),
            )}
          </ul>
        </div>
      </div>
      <div className={styles.actions}>
        {cta}
        <div className={styles.trustedBy}>
          <span>{format('trusted-by-teams-at')}</span>
          <img
            src={require('resources/images/upgrade-prompts/pinterest.svg')}
            alt="Pinterest"
            width="22"
          />
          <img
            src={require('resources/images/upgrade-prompts/bose.svg')}
            alt="Bose"
            width="91"
          />
          <img
            src={require('resources/images/upgrade-prompts/fortnite.svg')}
            alt="Fortnite"
            width="58"
          />
          <img
            src={require('resources/images/upgrade-prompts/peloton.svg')}
            alt="Peloton"
            width="85"
          />
        </div>
        <p className={styles.afterTrial}>{afterTrial}</p>
        {type === 'try-business-class' && (
          <a
            onClick={() => learnMoreBCButtonWithTracking()}
            href="/business-class"
            className={styles.secondaryCta}
          >
            {format([type, 'secondary-cta'])}
          </a>
        )}
      </div>
    </ErrorBoundary>
  );
};
